import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Avatar, message, Input, Popconfirm, Tooltip } from 'antd';
import { CrownOutlined, ClockCircleOutlined, CalendarOutlined, WalletOutlined, WarningOutlined, CopyOutlined, ReloadOutlined, KeyOutlined, IdcardOutlined, CustomerServiceOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { rechargeAPI, renewalAPI, resolveAvatarUrl, authAPI, cardBindAPI } from '../services/api';
import { copyToClipboard } from '../utils/clipboard';
import useGlassCard from '../hooks/useGlassCard';
import RechargeModal from './RechargeModal';
import './ProfileCard.css';

const POINTS_RATIO = 100;

interface CardBindInfo {
  balance: number;
  exclusiveBalance: number;
  boundCardKey: string | null;
  cardExpireTime: string | null;
  cardDisabled: boolean;
  orderAssistantExpiresAt: string | null;
}

const ProfileCard: React.FC = () => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<number>(0);
  const [exclusiveBalance, setExclusiveBalance] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [visible, setVisible] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [apiToken, setApiToken] = useState<string>('');
  const [tokenRefreshing, setTokenRefreshing] = useState(false);
  const [rechargeVisible, setRechargeVisible] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<{ wechatEnabled: boolean; alipayEnabled: boolean }>({ wechatEnabled: false, alipayEnabled: false });
  const [bindInfo, setBindInfo] = useState<CardBindInfo | null>(null);
  const [cardKeyInput, setCardKeyInput] = useState('');
  const [binding, setBinding] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  useGlassCard(cardRef);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  const fetchBindInfo = useCallback(async () => {
    try {
      const res = await cardBindAPI.getBindInfo();
      if (res.data?.status === 200 && res.data?.data) {
        setBindInfo(res.data.data);
        if (res.data.data.balance !== undefined) {
          setBalance(res.data.data.balance);
        }
        if (res.data.data.exclusiveBalance !== undefined) {
          setExclusiveBalance(res.data.data.exclusiveBalance);
        }
      }
    } catch {
      // 忽略绑定信息加载失败
    }
  }, []);

  useEffect(() => {
    fetchBindInfo();
  }, [fetchBindInfo]);

  const handleBindCard = useCallback(async () => {
    const key = cardKeyInput.trim();
    if (!key) {
      message.warning('请输入卡密');
      return;
    }
    setBinding(true);
    try {
      const res = await cardBindAPI.bindCard(key);
      if (res.data?.status === 200) {
        message.success('卡密绑定成功');
        setCardKeyInput('');
        fetchBindInfo();
      } else {
        message.error(res.data?.error || '绑定失败');
      }
    } catch {
      message.error('绑定失败，请重试');
    } finally {
      setBinding(false);
    }
  }, [cardKeyInput, fetchBindInfo]);

  const handleUnbindCard = useCallback(async () => {
    try {
      const res = await cardBindAPI.unbindCard();
      if (res.data?.status === 200) {
        message.success('卡密已解绑');
        fetchBindInfo();
      } else {
        message.error(res.data?.error || '解绑失败');
      }
    } catch {
      message.error('解绑失败，请重试');
    }
  }, [fetchBindInfo]);

  const handleRenewal = useCallback((productType: 'membership' | 'card' | 'order_assistant' = 'membership') => {
    window.dispatchEvent(new CustomEvent('show-renewal-modal', { detail: { productType } }));
  }, []);

  useEffect(() => {
    const checkPayment = async () => {
      try {
        const response = await renewalAPI.checkPaymentAvailable();
        if (response.data.status === 200) {
          setHasPaymentMethod(response.data.data?.available || false);
        } else {
          setHasPaymentMethod(false);
        }
      } catch {
        setHasPaymentMethod(false);
      }
    };
    checkPayment();
  }, []);

  useEffect(() => {
    const fetchPaymentConfig = async () => {
      try {
        const response = await rechargeAPI.getConfig();
        if (response.data && response.data.status === 200 && response.data.data) {
          const config = response.data.data;
          setPaymentConfig({
            wechatEnabled: Boolean(config.wechatRechargeEnabled || config.wechatEnabled),
            alipayEnabled: Boolean(config.alipayRechargeEnabled || config.alipayEnabled),
          });
        }
      } catch {
        setPaymentConfig({ wechatEnabled: false, alipayEnabled: false });
      }
    };
    fetchPaymentConfig();
  }, []);

  useEffect(() => {
    if (user?.api_token) {
      setApiToken(user.api_token);
    }
  }, [user?.api_token]);

  const handleRefreshToken = useCallback(async () => {
    setTokenRefreshing(true);
    try {
      const res = await authAPI.refreshApiToken();
      if (res.data?.status === 200 && res.data.api_token) {
        setApiToken(res.data.api_token);
        message.success('Token 已刷新');
      } else {
        message.error('刷新失败');
      }
    } catch {
      message.error('刷新失败');
    } finally {
      setTokenRefreshing(false);
    }
  }, []);

  const handleCopyToken = useCallback(async () => {
    if (!apiToken) {
      message.warning('暂无Token');
      return;
    }
    const ok = await copyToClipboard(apiToken);
    if (ok) message.success('已复制到剪贴板');
    else message.error('复制失败');
  }, [apiToken]);

  const handleOpenRecharge = useCallback(() => {
    if (!paymentConfig.wechatEnabled && !paymentConfig.alipayEnabled) {
      message.warning('充值功能暂未开启');
      return;
    }
    setRechargeVisible(true);
  }, [paymentConfig]);

  const fetchBalance = useCallback(async () => {
    try {
      const res = await rechargeAPI.getBalance();
      if (res.data?.data?.balance !== undefined) {
        setBalance(res.data.data.balance);
      }
    } catch {
      setBalance(0);
    }
  }, []);

  const handleRechargeSuccess = useCallback(() => {
    setRechargeVisible(false);
    fetchBalance();
  }, [fetchBalance]);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 6) return '夜深了';
    if (hour < 12) return '早上好';
    if (hour < 18) return '下午好';
    return '晚上好';
  };

  const getRoleLabel = () => {
    const role = user?.role || 'normal';
    const roleMap: Record<string, string> = {
      normal: '普通会员',
      vip: 'VIP会员',
      yearly_vip: '年费VIP会员',
      permanent: '永久会员',
      super_admin: '超级管理员',
    };
    return roleMap[role] || '普通会员';
  };

  const isPermanent = user?.role === 'permanent' || user?.role === 'super_admin' || user?.expires_at === '9999-12-31T23:59:59Z';

  const getDaysRemaining = () => {
    if (isPermanent || !user?.expires_at) return null;
    const expires = new Date(user.expires_at).getTime();
    const now = Date.now();
    const diffDays = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining();
  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0;
  const isExpired = daysRemaining !== null && daysRemaining <= 0;

  const formatCardDateTime = (timeStr: string | null) => {
    if (!timeStr) return '未设置';
    const date = new Date(timeStr);
    if (isNaN(date.getTime())) return timeStr;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  };

  const getExpiryStatus = (timeStr: string | null): { expiring: boolean; expired: boolean; days: number | null } => {
    if (!timeStr) return { expiring: false, expired: false, days: null };
    const expires = new Date(timeStr).getTime();
    if (isNaN(expires)) return { expiring: false, expired: false, days: null };
    const diffDays = Math.ceil((expires - Date.now()) / (1000 * 60 * 60 * 24));
    return {
      expiring: diffDays <= 7 && diffDays > 0,
      expired: diffDays <= 0,
      days: diffDays,
    };
  };

  const cardStatus = getExpiryStatus(bindInfo?.cardExpireTime || null);
  const assistantStatus = getExpiryStatus(bindInfo?.orderAssistantExpiresAt || null);

  const formatExpiry = () => {
    if (isPermanent) return '永久有效';
    if (!user?.expires_at) return '未设置';
    const date = new Date(user.expires_at);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    });
  };

  return (
    <div
      ref={cardRef}
      className={`profile-card ${visible ? 'profile-card-visible' : ''}`}
    >
      <div className="profile-header">
        <div className="profile-avatar">
          {user?.avatar_url ? (
            <Avatar
              size={64}
              src={resolveAvatarUrl(user.avatar_url)}
              style={{ boxShadow: '0 8px 20px rgba(80, 128, 255, 0.3)' }}
            />
          ) : (
            <span className="profile-avatar-emoji">👤</span>
          )}
        </div>
        <div className="profile-header-info">
          <div className="profile-greeting-label">尊敬的</div>
          <div className="profile-member-row">
            <span className={`profile-member-badge ${isPermanent ? 'profile-member-permanent' : ''}`}>
              <CrownOutlined style={{ marginRight: 4 }} />
              {getRoleLabel()}
            </span>
            <span className="profile-username-tag">{user?.username || '用户'}</span>
          </div>
          <div className="profile-greeting-text">{getGreeting()}，欢迎回来 ✨</div>
        </div>
      </div>

      <div className="profile-dual-row">
        <div className="profile-balance-card">
          <div className="profile-balance-info">
            <div className="profile-balance-label">
              <WalletOutlined style={{ marginRight: 6 }} />
              充值积分 <span className="profile-balance-sub">≈ ¥{balance.toFixed(2)}</span>
            </div>
            <div className="profile-balance-value">{(balance * POINTS_RATIO).toFixed(0)}</div>
            {exclusiveBalance > 0 && (
              <div className="profile-balance-exclusive">专享积分 {(exclusiveBalance * POINTS_RATIO).toFixed(0)}（仅接口消费）</div>
            )}
          </div>
          <div className="profile-balance-actions">
            <Button className="profile-btn-secondary" onClick={handleOpenRecharge}>充值</Button>
            {hasPaymentMethod && (
              <Button className="profile-btn-primary" onClick={() => handleRenewal('membership')}>续费会员</Button>
            )}
          </div>
        </div>

        <div className="profile-clock-card">
          <div className="profile-clock-label">
            <ClockCircleOutlined />
            当前时间
          </div>
          <div className="profile-clock-time">{formatTime(currentTime)}</div>
          <div className="profile-clock-date">{formatDate(currentTime)}</div>
        </div>

        <div className="profile-token-card">
          <div className="profile-token-info">
            <div className="profile-token-label">
              <KeyOutlined style={{ marginRight: 6 }} />
              API Token
            </div>
            <div className="profile-token-value" title={apiToken || '暂无Token'}>
              {apiToken ? `${apiToken.substring(0, 8)}${'•'.repeat(8)}${apiToken.substring(apiToken.length - 8)}` : '暂无Token'}
            </div>
          </div>
          <div className="profile-token-actions">
            <Button
              className="profile-btn-icon"
              icon={<ReloadOutlined spin={tokenRefreshing} />}
              onClick={handleRefreshToken}
              loading={tokenRefreshing}
              title="刷新Token"
            />
            <Button
              className="profile-btn-icon"
              icon={<CopyOutlined />}
              onClick={handleCopyToken}
              disabled={!apiToken}
              title="复制Token"
            />
          </div>
        </div>
      </div>

      <div className="profile-info-grid profile-info-grid-three">
        <div className={`profile-info-card ${isPermanent ? 'profile-info-card-permanent' : ''} ${isExpiringSoon ? 'profile-info-card-expiring' : ''} ${isExpired ? 'profile-info-card-expired' : ''}`}>
          <div className="profile-info-card-header">
            <CalendarOutlined className={`profile-info-icon ${isExpiringSoon || isExpired ? 'profile-info-icon-warning' : ''}`} />
            <span className="profile-info-label">会员到期时间</span>
            {(isExpiringSoon || isExpired) && (
              <WarningOutlined className="profile-expiring-warning-icon" />
            )}
          </div>
          <div className={`profile-info-time ${isPermanent ? 'profile-info-permanent-text' : ''} ${isExpiringSoon ? 'profile-info-time-expiring' : ''} ${isExpired ? 'profile-info-time-expired' : ''}`}>
            {formatExpiry()}
          </div>
          {!isPermanent && user?.expires_at && (
            <div className={`profile-info-date ${isExpiringSoon ? 'profile-info-date-expiring' : ''} ${isExpired ? 'profile-info-date-expired' : ''}`}>
              {isExpired ? '已到期' : `剩余 ${daysRemaining} 天`}
              {(isExpiringSoon || isExpired) && (
                <span className={`profile-expiring-hint-inline ${isExpired ? 'profile-expiring-hint-inline-expired' : ''}`}>
                  <WarningOutlined style={{ marginRight: 3, fontSize: 11 }} />
                  {isExpired ? '账户已到期，请尽快续费' : '即将到期，请及时续费'}
                </span>
              )}
            </div>
          )}
          {isPermanent && (
            <div className="profile-info-date profile-info-permanent-sub">尊享永久权益</div>
          )}
        </div>
        <div className={`profile-info-card ${cardStatus.expiring ? 'profile-info-card-expiring' : ''} ${cardStatus.expired ? 'profile-info-card-expired' : ''}`}>
          <div className="profile-info-card-header">
            <IdcardOutlined className={`profile-info-icon ${cardStatus.expiring || cardStatus.expired ? 'profile-info-icon-warning' : ''}`} />
            <span className="profile-info-label">卡密到期时间</span>
            {(cardStatus.expiring || cardStatus.expired) && (
              <WarningOutlined className="profile-expiring-warning-icon" />
            )}
          </div>
          {bindInfo?.boundCardKey ? (
            <>
              <div className={`profile-info-time profile-info-time-sm ${cardStatus.expiring ? 'profile-info-time-expiring' : ''} ${cardStatus.expired ? 'profile-info-time-expired' : ''}`}>
                {formatCardDateTime(bindInfo.cardExpireTime)}
              </div>
              <div className={`profile-info-date ${cardStatus.expired ? 'profile-info-date-expired' : ''}`}>
                {cardStatus.days !== null ? (cardStatus.expired ? '已到期' : `剩余 ${cardStatus.days} 天`) : '未设置'}
              </div>
              <div className="profile-card-actions">
                {hasPaymentMethod && (
                  <Button size="small" className="profile-btn-primary" onClick={() => handleRenewal('card')}>续费</Button>
                )}
                <Popconfirm
                  title="确定解绑当前卡密？"
                  description="解绑后可重新绑定"
                  onConfirm={handleUnbindCard}
                  okText="解绑"
                  cancelText="取消"
                >
                  <Button size="small" className="profile-btn-secondary" icon={<DisconnectOutlined />}>解绑</Button>
                </Popconfirm>
              </div>
            </>
          ) : (
            <>
              <div className="profile-info-time profile-info-time-sm profile-info-empty">未绑定卡密</div>
              <div className="profile-bind-row">
                <Input
                  size="small"
                  placeholder="输入卡密"
                  value={cardKeyInput}
                  onChange={(e) => setCardKeyInput(e.target.value)}
                  onPressEnter={handleBindCard}
                  maxLength={64}
                />
                <Button size="small" type="primary" icon={<LinkOutlined />} loading={binding} onClick={handleBindCard}>
                  绑定
                </Button>
              </div>
              <div className="profile-info-date">绑定卡密后显示到期时间</div>
            </>
          )}
        </div>
        <div className={`profile-info-card ${assistantStatus.expiring ? 'profile-info-card-expiring' : ''} ${assistantStatus.expired ? 'profile-info-card-expired' : ''}`}>
          <div className="profile-info-card-header">
            <CustomerServiceOutlined className={`profile-info-icon ${assistantStatus.expiring || assistantStatus.expired ? 'profile-info-icon-warning' : ''}`} />
            <span className="profile-info-label">接单助手到期时间</span>
            {(assistantStatus.expiring || assistantStatus.expired) && (
              <WarningOutlined className="profile-expiring-warning-icon" />
            )}
          </div>
          <div className={`profile-info-time profile-info-time-sm ${assistantStatus.expiring ? 'profile-info-time-expiring' : ''} ${assistantStatus.expired ? 'profile-info-time-expired' : ''}`}>
            {!bindInfo?.boundCardKey ? '未绑定卡密' : bindInfo?.orderAssistantExpiresAt ? formatCardDateTime(bindInfo.orderAssistantExpiresAt) : '未开通'}
          </div>
          {bindInfo?.boundCardKey ? (
            bindInfo?.orderAssistantExpiresAt ? (
              <div className={`profile-info-date ${assistantStatus.expired ? 'profile-info-date-expired' : ''}`}>
                {assistantStatus.days !== null ? (assistantStatus.expired ? '已到期' : `剩余 ${assistantStatus.days} 天`) : '未设置'}
              </div>
            ) : (
              <div className="profile-info-date">续费后即可使用</div>
            )
          ) : (
            <div className="profile-info-date">绑定卡密后可续费接单助手</div>
          )}
          {hasPaymentMethod && (
            <div className="profile-card-actions">
              <Tooltip title={!bindInfo?.boundCardKey ? '请先绑定卡密' : undefined}>
                <Button
                  size="small"
                  className="profile-btn-primary"
                  disabled={!bindInfo?.boundCardKey}
                  onClick={() => handleRenewal('order_assistant')}
                >
                  {bindInfo?.orderAssistantExpiresAt ? '续费' : '开通'}
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      <RechargeModal
        visible={rechargeVisible}
        onCancel={() => setRechargeVisible(false)}
        onSuccess={handleRechargeSuccess}
      />
    </div>
  );
};

export default ProfileCard;
